import React, { useState, useEffect, Fragment, useRef, useMemo, useCallback } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Field, FieldArray, getFormValues, initialize, change, reduxForm, isInvalid, getFormSyncErrors } from 'redux-form';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import moment from 'moment';
import {
    GET_LIST,
    provider,
} from '../../_utils';

import { TextInput, SelectInput } from '../../_Components';

// Генерируем массив часов от 7 до 22 (16 часов)
const time = Array.apply(null, {length: 16}).map(Number.call, Number).map((el) => (
    { 'id': (el+7)*60, 'name': `${el+7}:00` }
));

const formatTime = (minutes) => {
    if (minutes === null || minutes === undefined) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const parseTime = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
};

// Преобразует needed из объекта/массива в плоскую структуру для формы
const normalizeNeededForForm = (needed) => {
    if (!needed) return {};
    const result = {};
    
    if (Array.isArray(needed)) {
        // Массив: индекс соответствует часу
        needed.forEach((value, index) => {
            if (value !== null && value !== undefined && value !== '') {
                result[index] = String(value);
            }
        });
    } else if (typeof needed === 'object') {
        // Объект: ключ - это час
        Object.keys(needed).forEach(hour => {
            const value = needed[hour];
            if (value !== null && value !== undefined && value !== '') {
                result[hour] = String(value);
            }
        });
    }
    
    return result;
};

const DaySettingsModal = ({ 
    date, 
    projects, 
    initialize,
    change,
    dayFormValues,
    onInvalidChange,
    // Игнорируем asyncValidate и asyncBlurFields, если они переданы через props от Modal
    asyncValidate: _asyncValidate,
    asyncBlurFields: _asyncBlurFields,
    // ...restProps // Остальные пропсы
}) => {
    const [holiday, setHoliday] = useState(null);
    const [fullProjectsData, setFullProjectsData] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [holidayDataLoaded, setHolidayDataLoaded] = useState(false); // Флаг загрузки данных holidays
    // Локальное состояние для отслеживания типа дня для немедленного отображения полей
    const [currentType, setCurrentType] = useState(dayFormValues?.type || 'normal');
    
    // Ref для отслеживания статуса монтирования компонента
    const isMountedRef = useRef(true);
    // Ref для отслеживания предыдущей даты
    const prevDateRef = useRef(null);
    // Ref для хранения актуальных значений формы
    const dayFormValuesRef = useRef(dayFormValues);
    // Ref для отслеживания, был ли type изменен пользователем вручную
    const typeChangedByUserRef = useRef(false);
    // Ref для хранения значения type, которое пользователь выбрал вручную
    const userSelectedTypeRef = useRef(null);

    // Управление статусом монтирования
    useEffect(() => {
        isMountedRef.current = true;
        // Сбрасываем состояние при монтировании компонента
        setIsInitialized(false);
        setHolidayDataLoaded(false);
        setFullProjectsData([]);
        setHoliday(null);
        setCurrentType('normal');
        prevDateRef.current = null;
        typeChangedByUserRef.current = false; // Сбрасываем флаг при размонтировании
        userSelectedTypeRef.current = null; // Сбрасываем сохраненное значение при размонтировании
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Сбрасываем isInitialized при каждом открытии модального окна (когда date становится доступным)
    // Это гарантирует, что данные будут перезагружены при повторном открытии
    useEffect(() => {
        if (date) {
            // При открытии модального окна всегда сбрасываем isInitialized, чтобы перезагрузить данные
            setIsInitialized(false);
            setHolidayDataLoaded(false);
            // Сбрасываем prevDateRef, чтобы при следующем открытии с той же датой данные перезагрузились
            prevDateRef.current = null;
        }
    }, [date]);

    // Сбрасываем состояние при изменении даты
    useEffect(() => {
        if (!date) return;
        if (!isMountedRef.current) return;
        
        // Проверяем, изменилась ли дата
        const dateStr = moment(date).format('YYYY-MM-DD');
        const prevDateStr = prevDateRef.current ? moment(prevDateRef.current).format('YYYY-MM-DD') : null;
        
        // Всегда сбрасываем флаги загрузки при изменении даты или при первом рендере
        if (dateStr !== prevDateStr) {
            // Дата изменилась - сбрасываем все состояние
            setHoliday(null);
            setIsInitialized(false);
            setFullProjectsData([]);
            setHolidayDataLoaded(false);
            setCurrentType('normal');
            initialize({
                type: 'normal',
                scheduleDay: null,
                name: '',
                projects: []
            });
            prevDateRef.current = date;
        } else {
            // Та же дата, но компонент пересоздан или открыт заново - сбрасываем только флаги загрузки
            setIsInitialized(false);
            setHolidayDataLoaded(false);
            // Не сбрасываем prevDateRef, чтобы не зациклиться
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]); // initialize не должен быть в зависимостях

    // Загружаем информацию о празднике для этой даты
    useEffect(() => {
        const fetchHoliday = async () => {
            if (!date) return;
            if (!isMountedRef.current) return;
            try {
                const { data } = await provider(
                    GET_LIST,
                    '/holidays',
                    {
                        filter: {
                            date: {
                                $gte: moment(date).startOf('day').toDate(),
                                $lte: moment(date).endOf('day').toDate(),
                            },
                        },
                        pagination: { page: 1, perPage: 1 },
                    }
                );
                if (!isMountedRef.current) return;
                if (data && data.length > 0) {
                    const holidayData = data[0];
                    setHoliday(holidayData);
                    // Не обновляем currentType, если пользователь уже изменил type вручную
                    if (!typeChangedByUserRef.current) {
                        const holidayType = holidayData.type === 'holiday' ? 'holiday' : 'working_weekend';
                        setCurrentType(holidayType);
                    }
                    setHolidayDataLoaded(true);
                    // Не инициализируем форму здесь, ждем загрузки project-day-schedules
                } else {
                    setHoliday(null);
                    // Не обновляем currentType, если пользователь уже изменил type вручную
                    if (!typeChangedByUserRef.current) {
                        setCurrentType('normal');
                    }
                    setHolidayDataLoaded(true);
                    // Не инициализируем форму здесь, ждем загрузки project-day-schedules
                }
            } catch (error) {
                console.error('Error fetching holiday:', error);
                if (!isMountedRef.current) return;
                setHoliday(null);
                // Не обновляем currentType, если пользователь уже изменил type вручную
                if (!typeChangedByUserRef.current) {
                    setCurrentType('normal');
                }
                setHolidayDataLoaded(true);
            }
        };
        fetchHoliday();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]); // Убрали initialize и dayFormValues из зависимостей, чтобы избежать бесконечного цикла

    // Загружаем полные данные проектов
    useEffect(() => {
        const fetchFullProjects = async () => {
            if (!date) return;
            try {
                const { data: campaignsData } = await provider(
                    GET_LIST,
                    '/campaigns',
                    {
                        filter: { status: 'Active' },
                        sort: { field: 'name', order: 'DESC' },
                        pagination: { page: 1, perPage: 1000 },
                    }
                );
                if (isMountedRef.current) {
                    setFullProjectsData(campaignsData || []);
                }
            } catch (error) {
                console.error('Error fetching full projects data:', error);
                if (isMountedRef.current) {
                    setFullProjectsData([]);
                }
            }
        };
        fetchFullProjects();
    }, [date]);

    // Загружаем графики проектов и инициализируем форму
    useEffect(() => {
        const fetchProjectSchedules = async () => {
            // Ждем загрузки данных holidays и полных данных проектов
            if (!date || !fullProjectsData || fullProjectsData.length === 0 || isInitialized || !holidayDataLoaded) return;
            
            try {
                const dateStr = moment(date).format('YYYY-MM-DD');
                
                // Загружаем данные из БД
                const response = await provider(
                    GET_LIST,
                    '/project-day-schedules',
                    {
                        filter: { date: dateStr },
                        pagination: { page: 1, perPage: 1000 },
                    }
                );
                
                // Обрабатываем ответ - provider возвращает { data, total }
                const schedulesData = response?.data || [];
                
                // Отладочный вывод (можно убрать после проверки)
                // console.log('Loaded schedulesData:', schedulesData);
                // console.log('Projects:', projects.map(p => ({ id: p._id, name: p.name })));
                
                // Определяем день недели для графика
                let effectiveDayNum = null;
                if (holiday?.scheduleDay) {
                    effectiveDayNum = holiday.scheduleDay;
                } else {
                    const dayNum = moment(date).day() === 0 ? 7 : moment(date).day();
                    effectiveDayNum = dayNum;
                }
                
                // Формируем массив проектов для формы
                // Используем fullProjectsData вместо projects, чтобы включить все проекты
                const projectsArray = fullProjectsData.map(project => {
                    // Проверяем данные из БД
                    // projectId может быть строкой или объектом
                    const existingSchedule = schedulesData.find(s => {
                        if (!s || !s.projectId) return false;
                        // Преобразуем projectId в строку для сравнения
                        let scheduleProjectId = s.projectId;
                        if (typeof scheduleProjectId === 'object') {
                            scheduleProjectId = scheduleProjectId._id || scheduleProjectId.id || String(scheduleProjectId);
                        } else {
                            scheduleProjectId = String(scheduleProjectId);
                        }
                        const projectId = String(project._id || project.id);
                        return scheduleProjectId === projectId;
                    });
                    
                    if (existingSchedule) {
                        // Используем данные из БД
                        const neededSource = existingSchedule.needed !== undefined 
                            ? existingSchedule.needed 
                            : (project.schedule && effectiveDayNum && project.schedule[effectiveDayNum]?.needed) || null;
                        return {
                            projectId: project._id,
                            start: existingSchedule.start || 540,
                            end: existingSchedule.end || 1080,
                            active: existingSchedule.active !== false,
                            needed: normalizeNeededForForm(neededSource),
                        };
                    } else {
                        // Используем стандартный график проекта
                        if (project.schedule && effectiveDayNum && project.schedule[effectiveDayNum]) {
                            const daySchedule = project.schedule[effectiveDayNum];
                            return {
                                projectId: project._id,
                                start: daySchedule.start || 540,
                                end: daySchedule.end || 1080,
                                active: !!daySchedule, // Активен только если есть график для выбранного дня
                                needed: normalizeNeededForForm(daySchedule.needed),
                            };
                        } else {
                            return {
                                projectId: project._id,
                                start: 540,
                                end: 1080,
                                active: false,
                                needed: {},
                            };
                        }
                    }
                });
                
                // Используем данные из holidays для type, scheduleDay, name
                let formType = 'normal';
                let formScheduleDay = null;
                let formName = '';
                
                if (holiday) {
                    formType = holiday.type === 'holiday' ? 'holiday' : 'working_weekend';
                    formScheduleDay = holiday.scheduleDay || null;
                    formName = holiday.name || '';
                }
                
                if (!isMountedRef.current) return;
                
                // Инициализируем форму со всеми данными: из holidays и project-day-schedules
                // Не перезаписываем type, если пользователь уже изменил его вручную
                const finalType = typeChangedByUserRef.current && userSelectedTypeRef.current !== null
                    ? userSelectedTypeRef.current
                    : formType;
                
                initialize({
                    type: finalType,
                    scheduleDay: formScheduleDay,
                    name: formName,
                    projects: projectsArray,
                });
                
                setIsInitialized(true);
                // НЕ сбрасываем флаг здесь - он должен сохраняться до закрытия модального окна или сохранения
            } catch (error) {
                console.error('Error fetching project schedules:', error);
                // В случае ошибки используем стандартные графики
                const dayNum = moment(date).day() === 0 ? 7 : moment(date).day();
                const projectsArray = projects.map(project => {
                    const fullProject = fullProjectsData.find(p => p._id === project._id);
                    if (fullProject && fullProject.schedule && fullProject.schedule[dayNum]) {
                        const daySchedule = fullProject.schedule[dayNum];
                        return {
                            projectId: project._id,
                            start: daySchedule.start || 540,
                            end: daySchedule.end || 1080,
                            active: !!daySchedule, // Активен только если есть график для выбранного дня
                        };
                    } else if (project.schedule) {
                        return {
                            projectId: project._id,
                            start: project.schedule.start || 540,
                            end: project.schedule.end || 1080,
                            active: !!project.schedule, // Активен только если есть график
                        };
                    } else {
                        return {
                            projectId: project._id,
                            start: 540,
                            end: 1080,
                            active: false,
                        };
                    }
                });
                
                // Используем данные из holidays для type, scheduleDay, name
                let formType = 'normal';
                let formScheduleDay = null;
                let formName = '';
                
                if (holiday) {
                    formType = holiday.type === 'holiday' ? 'holiday' : 'working_weekend';
                    formScheduleDay = holiday.scheduleDay || null;
                    formName = holiday.name || '';
                }
                
                if (!isMountedRef.current) return;
                
                // Не перезаписываем type, если пользователь уже изменил его вручную
                const finalType = typeChangedByUserRef.current && dayFormValues?.type 
                    ? dayFormValues.type 
                    : formType;
                
                initialize({
                    type: finalType,
                    scheduleDay: formScheduleDay,
                    name: formName,
                    projects: projectsArray,
                });
                setIsInitialized(true);
                // НЕ сбрасываем флаг здесь - он должен сохраняться до закрытия модального окна или сохранения
            }
        };
        
        // Ждем загрузки данных holidays и полных данных проектов
        // Вызываем fetchProjectSchedules только если данные еще не инициализированы
        // И только если пользователь не изменил type вручную (чтобы не перезаписать его выбор)
        if (!isInitialized && fullProjectsData.length > 0 && holidayDataLoaded && !typeChangedByUserRef.current) {
            fetchProjectSchedules();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, fullProjectsData.length, holidayDataLoaded, isInitialized]); // Убрали holiday из зависимостей, чтобы не переинициализировать при изменении

    // Синхронизируем локальное состояние с dayFormValues
    const formType = dayFormValues?.type;
    useEffect(() => {
        // Обновляем currentType только если форма уже инициализирована и значение действительно изменилось
        // НЕ обновляем если пользователь только что изменил type вручную
        if (isInitialized && formType && formType !== currentType && !typeChangedByUserRef.current) {
            setCurrentType(formType);
        }
        // Обновляем ref при изменении dayFormValues
        dayFormValuesRef.current = dayFormValues;
    }, [formType, currentType, dayFormValues, isInitialized]);

    // Обновляем scheduleDay при изменении type
    useEffect(() => {
        if (!formType) return;
        // Не обновляем scheduleDay, если пользователь только что изменил type вручную
        // (это предотвратит лишние обновления и возможные конфликты)
        if (typeChangedByUserRef.current) {
            return;
        }
        
        let newScheduleDay = dayFormValues?.scheduleDay;
        let shouldUpdate = false;
        
        if (formType === 'holiday' && (!newScheduleDay || (newScheduleDay !== 6 && newScheduleDay !== 7))) {
            newScheduleDay = 6;
            shouldUpdate = true;
        } else if (formType === 'working_weekend' && (!newScheduleDay || newScheduleDay < 1 || newScheduleDay > 5)) {
            newScheduleDay = 1;
            shouldUpdate = true;
        } else if (formType === 'normal' && newScheduleDay !== null) {
            newScheduleDay = null;
            shouldUpdate = true;
        }
        
        if (shouldUpdate) {
            // Используем setTimeout, чтобы избежать проблем с промисами из change
            setTimeout(() => {
                if (isMountedRef.current) {
                    change('scheduleDay', newScheduleDay);
                }
            }, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formType]); // Отслеживаем изменения type, убрали change из зависимостей

    // Функция для обновления графиков проектов на основе дня недели
    const updateProjectsSchedules = useCallback((scheduleDayValue) => {
        if (!scheduleDayValue || !fullProjectsData.length) {
            console.warn('updateProjectsSchedules: missing scheduleDayValue or fullProjectsData', { scheduleDayValue, fullProjectsDataLength: fullProjectsData.length });
            return;
        }
        
        // Используем setTimeout, чтобы дождаться обновления dayFormValues после изменения scheduleDay
        setTimeout(() => {
            // Получаем актуальные значения формы из props (приоритет) или ref
            // Используем dayFormValues (props) как основной источник, так как он обновляется синхронно с Redux Form
            let currentFormValues = dayFormValues || dayFormValuesRef.current;
            
            // Если пользователь изменил type вручную, ВСЕГДА используем сохраненное значение из userSelectedTypeRef
            // Иначе используем currentType или значение из формы
            const currentTypeValue = typeChangedByUserRef.current && userSelectedTypeRef.current !== null
                ? userSelectedTypeRef.current
                : (currentType !== 'normal' ? currentType : (dayFormValues?.type || currentFormValues?.type || 'normal'));
            
            // Если форма еще не инициализирована, создаем проекты из fullProjectsData
            if (!currentFormValues?.projects || currentFormValues.projects.length === 0) {
                if (!isInitialized) {
                    console.warn('updateProjectsSchedules: Form not initialized yet, skipping');
                    return;
                }
                // Если форма инициализирована, но проектов нет, создаем их из fullProjectsData
                console.log('updateProjectsSchedules: Creating projects from fullProjectsData');
                const projectsFromData = fullProjectsData.map((project) => {
                    const daySchedule = project.schedule && project.schedule[scheduleDayValue] 
                        ? project.schedule[scheduleDayValue]
                        : null;
                    return {
                        projectId: project._id,
                        start: daySchedule?.start || 540,
                        end: daySchedule?.end || 1080,
                        active: !!daySchedule, // Активен только если есть график для выбранного дня
                        needed: normalizeNeededForForm(daySchedule?.needed),
                    };
                });
                currentFormValues = {
                    ...currentFormValues,
                    projects: projectsFromData,
                };
            }
            
            console.log('updateProjectsSchedules: Updating projects for scheduleDay', scheduleDayValue, 'projects count:', currentFormValues.projects.length);
            
            // Формируем обновленный массив проектов с графиками из выбранного дня недели
            const updatedProjects = currentFormValues.projects.map((projectForm, index) => {
                // Нормализуем projectId для сравнения
                const formProjectId = typeof projectForm.projectId === 'string' 
                    ? projectForm.projectId 
                    : (typeof projectForm.projectId === 'object' 
                        ? (projectForm.projectId?._id || projectForm.projectId?.id || String(projectForm.projectId))
                        : String(projectForm.projectId));
                
                const fullProject = fullProjectsData.find(p => {
                    const projectId = typeof p._id === 'string' ? p._id : (p._id?.toString ? p._id.toString() : String(p._id));
                    return projectId === formProjectId;
                });
                
                if (fullProject && fullProject.schedule && fullProject.schedule[scheduleDayValue]) {
                    const daySchedule = fullProject.schedule[scheduleDayValue];
                    console.log(`updateProjectsSchedules: Found schedule for project ${fullProject.name}, day ${scheduleDayValue}`, daySchedule);
                    
                    // Всегда используем график из выбранного дня недели
                    // Сохраняем исходное состояние активности (если было false, остается false)
                    return {
                        ...projectForm,
                        start: daySchedule.start || 540,
                        end: daySchedule.end || 1080,
                        active: projectForm.active === undefined ? true : projectForm.active, // Сохраняем исходное значение active
                        // Всегда используем needed из графика выбранного дня
                        needed: normalizeNeededForForm(daySchedule.needed),
                    };
                } else {
                    console.warn(`updateProjectsSchedules: No schedule found for project ${formProjectId}, day ${scheduleDayValue}`, { fullProject: !!fullProject, hasSchedule: fullProject?.schedule ? !!fullProject.schedule[scheduleDayValue] : false });
                    // Если графика для выбранного дня нет, проект должен быть неактивным
                    return {
                        ...projectForm,
                        active: false,
                    };
                }
            });
            
            // Также обновляем всю форму через initialize для полной синхронизации
            // Сохраняем все текущие значения формы, включая type, name и другие поля
            // ВСЕГДА используем сохраненное значение из userSelectedTypeRef, если пользователь изменил его вручную
            // Иначе используем currentTypeValue
            const finalTypeForUpdate = typeChangedByUserRef.current && userSelectedTypeRef.current !== null
                ? userSelectedTypeRef.current
                : currentTypeValue;
            
            initialize({
                type: finalTypeForUpdate, // Используем актуальное значение type из формы
                name: currentFormValues?.name || '',
                scheduleDay: scheduleDayValue,
                projects: updatedProjects,
            }, false); // keepDirty: false - чтобы сбросить dirty флаги
        }, 100); // Уменьшили задержку, так как убрали множественные вызовы change
    }, [fullProjectsData, initialize, dayFormValues, isInitialized, currentType]);

    // Обновляем графики проектов при изменении scheduleDay через useEffect (резервный механизм)
    useEffect(() => {
        // Обновляем графики только для праздничных дней и рабочих выходных
        if (!isInitialized || !dayFormValues?.scheduleDay || !fullProjectsData.length || formType === 'normal') return;
        
        // Проверяем, нужно ли обновить графики
        const needsUpdate = dayFormValues.projects?.some((projectForm, index) => {
            const fullProject = fullProjectsData.find(p => p._id === projectForm.projectId);
            if (fullProject && fullProject.schedule && fullProject.schedule[dayFormValues.scheduleDay]) {
                const daySchedule = fullProject.schedule[dayFormValues.scheduleDay];
                return projectForm.start !== (daySchedule.start || 540) || 
                       projectForm.end !== (daySchedule.end || 1080);
            }
            return false;
        });
        
        if (needsUpdate) {
            updateProjectsSchedules(dayFormValues.scheduleDay);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dayFormValues?.scheduleDay, isInitialized, fullProjectsData.length, formType, updateProjectsSchedules]);

    // Используем useMemo для вычисления опций на основе currentType и formType
    const scheduleDayOptions = useMemo(() => {
        // Используем currentType в первую очередь, так как он обновляется немедленно при изменении
        const type = currentType || formType || 'normal';
        
        if (type === 'holiday') {
            return [
                { id: 6, name: 'Суббота' },
                { id: 7, name: 'Воскресенье' },
            ];
        } else if (type === 'working_weekend') {
            return [
                { id: 1, name: 'Понедельник' },
                { id: 2, name: 'Вторник' },
                { id: 3, name: 'Среда' },
                { id: 4, name: 'Четверг' },
                { id: 5, name: 'Пятница' },
            ];
        }
        // Возвращаем пустой массив для 'normal', но это не должно быть проблемой,
        // так как поле scheduleDay отображается только когда type !== 'normal'
        return [];
    }, [currentType, formType]);

    // Передаем invalid в родительский компонент через onInvalidChange, если он есть
    useEffect(() => {
        if (typeof onInvalidChange === 'function') {
            // Вычисляем invalid вручную, проверяя наличие ошибок валидации
            let hasErrors = false;
            
            if (dayFormValues && dayFormValues.projects && Array.isArray(dayFormValues.projects)) {
                hasErrors = dayFormValues.projects.some((project) => {
                    // Пропускаем неактивные проекты
                    if (!project || project.active === false) return false;
                    
                    const startTime = project.start !== undefined && project.start !== null ? project.start : 540;
                    const endTime = project.end !== undefined && project.end !== null ? project.end : 1080;
                    
                    // Проверяем каждый час от 7 до 22
                    for (let hour = 7; hour <= 22; hour++) {
                        const hourStart = hour * 60;
                        const hourEnd = (hour + 1) * 60;
                        const isHourInRange = hourStart < endTime && hourEnd > startTime;
                        
                        // Если час в диапазоне и значение пустое - это ошибка
                        if (isHourInRange) {
                            const hourValue = project.needed?.[hour];
                            if (hourValue === '' || hourValue === null || hourValue === undefined) {
                                return true; // Найдена ошибка
                            }
                        }
                    }
                    return false;
                });
            }
            
            // Вызываем onInvalidChange с результатом проверки
            onInvalidChange(hasErrors);
        }
    }, [dayFormValues, onInvalidChange]);

    return (
        <Box sx={{ p: 3, minWidth: 600 }}>
            <form>
                <Box sx={{ mb: 1 }}>
                    <Field
                        name="type"
                        component={(props) => {
                            const { input, ...rest } = props;
                            return (
                                <SelectInput
                                    {...rest}
                                    input={{
                                        ...input,
                                        onChange: (event) => {
                                            const value = event.target.value;
                                            input.onChange(value);
                                            setCurrentType(value);
                                            // Отмечаем, что пользователь изменил type вручную и сохраняем выбранное значение
                                            typeChangedByUserRef.current = true;
                                            userSelectedTypeRef.current = value;
                                            
                                            // Если выбран "Обычный день", обновляем графики для дня недели выбранной даты
                                            if (value === 'normal' && date && fullProjectsData.length > 0 && isInitialized) {
                                                const dayNum = moment(date).day() === 0 ? 7 : moment(date).day();
                                                // Обновляем графики проектов
                                                // НЕ сбрасываем флаг здесь - он должен сохраняться до сохранения или закрытия модального окна
                                                setTimeout(() => {
                                                    updateProjectsSchedules(dayNum);
                                                }, 200);
                                            }
                                            // НЕ сбрасываем флаг здесь - он должен сохраняться до сохранения или закрытия модального окна
                                        }
                                    }}
                                />
                            );
                        }}
                        label="Тип дня"
                        options={[
                            { id: 'normal', name: 'Обычный день' },
                            { id: 'holiday', name: 'Праздничный день (выходной)' },
                            { id: 'working_weekend', name: 'Рабочий выходной' },
                        ]}
                        style={{ marginBottom: 16 }}
                    />

                    {currentType && currentType !== 'normal' && (
                        <>
                            <Field
                                name="name"
                                component={TextInput}
                                label="Название"
                                placeholder={currentType === 'holiday' ? 'Праздничный день' : 'Рабочий выходной'}
                                style={{ marginBottom: 16 }}
                            />
                            <Field
                                name="scheduleDay"
                                component={(props) => {
                                    const { input, ...rest } = props;
                                    return (
                                        <SelectInput
                                            {...rest}
                                            input={{
                                                ...input,
                                                onChange: (event) => {
                                                    const value = event.target.value;
                                                    input.onChange(value);
                                                    // Обновляем графики проектов при изменении дня недели
                                                    // Проверяем, что форма инициализирована перед обновлением
                                                    if (!isInitialized) {
                                                        console.warn('updateProjectsSchedules: Form not initialized, waiting...');
                                                        // Ждем инициализации формы
                                                        setTimeout(() => {
                                                            if (isInitialized) {
                                                                updateProjectsSchedules(value);
                                                            }
                                                        }, 300);
                                                    } else {
                                                        // Используем небольшую задержку, чтобы дождаться обновления формы
                                                        setTimeout(() => {
                                                            updateProjectsSchedules(value);
                                                        }, 100);
                                                    }
                                                }
                                            }}
                                        />
                                    );
                                }}
                                label="График работы"
                                options={scheduleDayOptions}
                                style={{ marginBottom: 8 }}
                            />
                        </>
                    )}
                </Box>

                <Typography variant="h6" gutterBottom sx={{ mt: 1, mb: 1 }}>
                    Графики проектов
                </Typography>

                <TableContainer component={Paper} sx={{ maxHeight: 600, mb: 1, overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell>Проект</TableCell>
                                <TableCell>Старт</TableCell>
                                <TableCell>Конец</TableCell>
                                {time.map((k, p) => (
                                    <TableCell key={k.id} sx={{ minWidth: 40, padding: '4px' }}>
                                        {k.id / 60}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fullProjectsData && fullProjectsData.length > 0 ? (
                                <FieldArray name="projects" component={renderProjects} projects={fullProjectsData} dayFormValues={dayFormValues} time={time} onInvalidChange={onInvalidChange} />
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4 + time.length} align="center">
                                        <Typography variant="body2" color="textSecondary">
                                            Нет проектов
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </form>
        </Box>
    );
};

// Компонент для поля времени с отслеживанием изменений
const TimeFieldWithTracking = ({ input, onValueChange, parseTimeFn, formatTimeFn, parse, format, ...rest }) => {
    useEffect(() => {
        if (input.value !== null && input.value !== undefined && parseTimeFn) {
            const parsed = typeof input.value === 'number' 
                ? input.value 
                : parseTimeFn(input.value);
            if (parsed !== null && parsed !== undefined) {
                onValueChange(parsed);
            }
        }
    }, [input.value, parseTimeFn, onValueChange]);
    
    // Исключаем parse и format из rest, чтобы они не попали в DOM
    const { parse: _, format: __, disabled, ...textInputProps } = rest;
    
    return (
        <TextInput
            {...textInputProps}
            input={input}
            type="time"
            disabled={disabled}
            inputProps={{ 
                step: 300,
                style: {
                    backgroundColor: 'transparent',
                    color: disabled ? '#9e9e9e' : 'inherit',
                }
            }}
            style={{ width: 60 }}
        />
    );
};

// Компонент для строки проекта с динамическим отслеживанием start и end
const ProjectRow = ({ projectField, project, index, time, dayFormValues, onInvalidChange }) => {
    const [startTime, setStartTime] = useState(() => {
        const projectValue = dayFormValues?.projects?.[index];
        return projectValue?.start !== undefined && projectValue?.start !== null 
            ? projectValue.start 
            : 540;
    });
    
    const [endTime, setEndTime] = useState(() => {
        const projectValue = dayFormValues?.projects?.[index];
        return projectValue?.end !== undefined && projectValue?.end !== null 
            ? projectValue.end 
            : 1080;
    });
    
    const projectValue = dayFormValues?.projects?.[index];
    const projectStart = projectValue?.start;
    const projectEnd = projectValue?.end;
    
    // Обновляем startTime и endTime при изменении dayFormValues
    useEffect(() => {
        if (projectStart !== undefined && projectStart !== null) {
            setStartTime(projectStart);
        }
        if (projectEnd !== undefined && projectEnd !== null) {
            setEndTime(projectEnd);
        }
    }, [projectStart, projectEnd]);
    
    // Получаем значение active из формы через Field компонент
    return (
        <Field
            name={`${projectField}.active`}
            component={({ input }) => {
                // Используем значение напрямую из формы
                const isActiveState = input.value !== false;
                
                return (
                    <TableRow 
                        key={project._id || index} 
                        sx={{ 
                            backgroundColor: 'transparent',
                        }}
                    >
                        <TableCell>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        sx={{
                                            padding: 0,
                                        }}
                                        checked={isActiveState}
                                        onChange={(e) => {
                                            input.onChange(e.target.checked);
                                        }}
                                        size="small"
                                    />
                                }
                                sx={{ margin: 0 }}
                            />
                        </TableCell>
            <TableCell
                sx={{
                    fontWeight: 600,
                    color:  '#0a2b61',
                    fontSize: '0.65rem',
                }}
            >
                {project.name}
            </TableCell>
           
            <TableCell>
                <Field
                    name={`${projectField}.start`}
                    component={TimeFieldWithTracking}
                    parse={(value) => parseTime(value)}
                    format={(value) => formatTime(value)}
                    parseTimeFn={parseTime}
                    formatTimeFn={formatTime}
                    onValueChange={setStartTime}
                    disabled={!isActiveState}
                />
            </TableCell>
            <TableCell>
                <Field
                    name={`${projectField}.end`}
                    component={TimeFieldWithTracking}
                    parse={(value) => parseTime(value)}
                    format={(value) => formatTime(value)}
                    parseTimeFn={parseTime}
                    formatTimeFn={formatTime}
                    onValueChange={setEndTime}
                    disabled={!isActiveState}
                />
            </TableCell>
            {time.map((k, p) => {
                const hour = k.id / 60;
                const hourStart = k.id;
                const hourEnd = k.id + 60;
                // Отключаем поле, если час не пересекается с интервалом start-end
                // Час активен, если он пересекается с интервалом [startTime, endTime)
                // Пересечение: hourStart < endTime && hourEnd > startTime
                const isHourInRange = hourStart < endTime && hourEnd > startTime;
                const disabled = !isActiveState || !isHourInRange;
                
                return (
                    <TableCell key={k.id} sx={{ padding: '2px' }}>
                        <Field
                            style={{ width: 24 }}
                            name={`${projectField}.needed.${hour}`}
                            component={({ input, meta, ...rest }) => {
                                // Подсвечиваем только если проект активен, час в диапазоне и значение пустое
                                const isEmpty = isActiveState && isHourInRange
                                    && (input.value === '' || input.value === null || input.value === undefined);
                                
                                return (
                                    <TextInput
                                        {...rest}
                                        input={input}
                                        meta={meta}
                                        disabled={disabled}
                                        inputProps={{ 
                                            style: { 
                                                textAlign: 'center',
                                                padding: '2px 4px',
                                                fontSize: '0.75rem',
                                                backgroundColor: isEmpty ? '#ffebee' :  'transparent',
                                                border: isEmpty ? '1px solid #f44336' : 'none',
                                                color: disabled ? '#9e9e9e' : 'inherit',
                                            }
                                        }}
                                    />
                                );
                            }}
                        />
                    </TableCell>
                );
            })}
                    </TableRow>
                );
            }}
        />
    );
};

const renderProjects = ({ fields, projects, dayFormValues, time, onInvalidChange }) => {
    return (
        <Fragment>
            {fields.map((projectField, index) => {
                const project = projects[index];
                if (!project) return null;
                
                return (
                    <ProjectRow
                        key={project._id || index}
                        projectField={projectField}
                        project={project}
                        index={index}
                        time={time}
                        dayFormValues={dayFormValues}
                        onInvalidChange={onInvalidChange}
                    />
                );
            })}
        </Fragment>
    );
};

const mapStateToProps = (state) => {
    const dayFormValues = getFormValues('daySettingsForm')(state);
    const syncErrors = getFormSyncErrors('daySettingsForm')(state);
    const dayFormInvalid = isInvalid('daySettingsForm')(state);
    
    // Дополнительная проверка ошибок валидации
    const hasValidationErrors = syncErrors && (
        Object.keys(syncErrors).length > 0 || 
        (syncErrors.projects && syncErrors.projects.some(p => p && Object.keys(p).length > 0))
    );
    
    return {
        dayFormValues: dayFormValues,
        dayFormInvalid: dayFormInvalid || hasValidationErrors,
        initialValues: {
            type: 'normal',
            scheduleDay: null,
            name: '',
            projects: [],
        },
    };
};

const mapDispatchToProps = (dispatch) => ({
    initialize: (values) => dispatch(initialize('daySettingsForm', values)),
    change: (field, value) => dispatch(change('daySettingsForm', field, value)),
});

// Функция валидации для проверки пустых активных часов
const validate = (values) => {
    const errors = {};
    
    if (values.projects && Array.isArray(values.projects)) {
        values.projects.forEach((project, projectIndex) => {
            if (!project || project.active === false) return;
            
            const startTime = project.start !== undefined && project.start !== null ? project.start : 540;
            const endTime = project.end !== undefined && project.end !== null ? project.end : 1080;
            
            // Проверяем каждый час от 7 до 22
            const projectErrors = {};
            for (let hour = 7; hour <= 22; hour++) {
                const hourStart = hour * 60;
                const hourEnd = (hour + 1) * 60;
                
                // Проверяем, попадает ли час в интервал start-end
                const isHourInRange = hourStart < endTime && hourEnd > startTime;
                
                if (isHourInRange) {
                    const hourValue = project.needed?.[hour];
                    if (hourValue === '' || hourValue === null || hourValue === undefined) {
                        projectErrors.needed = projectErrors.needed || {};
                        projectErrors.needed[hour] = 'Обязательное поле';
                    }
                }
            }
            
            if (Object.keys(projectErrors).length > 0) {
                errors.projects = errors.projects || [];
                errors.projects[projectIndex] = projectErrors;
            }
        });
    }
    
    return errors;
};

const enhance = compose(
    connect(mapStateToProps, mapDispatchToProps),
    reduxForm({
        form: 'daySettingsForm',
        enableReinitialize: true,
        destroyOnUnmount: true,
        validate: validate,
        touchOnBlur: true,
        touchOnChange: true,
        // Явно отключаем asyncValidate, чтобы избежать ошибок
        // Если asyncValidate передается через props от Modal, он будет переопределен
        asyncValidate: () => Promise.resolve(),
        asyncBlurFields: [],
    })
);

export default enhance(DaySettingsModal);
